import { Outlet, NavLink, Link } from "react-router-dom";
import { useState } from "react";
import { Menu, X, ShieldCheck, Phone, Mail, MapPin } from "lucide-react";
import { LOGO_URL } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import CurrencySwitcher from "@/components/CurrencySwitcher";
import WhatsAppFab from "@/components/WhatsAppFab";

const navItems = [
  { to: "/shop", label: "Shop" },
  { to: "/repairs", label: "Repairs" },
  { to: "/trade-in", label: "Trade-in" },
  { to: "/branches", label: "Branches" },
  { to: "/pos-system", label: "POS Software" },
  { to: "/app", label: "Get App" },
  { to: "/contact", label: "Contact" },
];

function Navbar() {
  const [open, setOpen] = useState(false);
  const { user } = useAuth();

  return (
    <header className="sticky top-0 z-50 backdrop-blur-xl bg-white/85 border-b border-slate-200" data-testid="global-navbar">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3" data-testid="nav-logo">
          <img src={LOGO_URL} alt="JAS" className="h-12 w-12 rounded-full object-cover ring-1 ring-slate-200" />
          <div className="leading-tight">
            <div className="font-display font-black text-[#1a2d5e] text-lg tracking-tight">JAS</div>
            <div className="overline text-[10px]">Unlimited Solutions</div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-1">
          {navItems.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={`nav-${n.to.replace("/", "")}`}
              className={({ isActive }) =>
                `px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                  isActive
                    ? "bg-[#1a2d5e] text-white"
                    : "text-slate-700 hover:bg-slate-100"
                }`
              }
            >
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-2">
          <CurrencySwitcher compact />
          {user ? (
            <Link to="/admin" data-testid="nav-admin-link" className="px-4 py-2 text-sm font-semibold border border-[#1a2d5e] text-[#1a2d5e] rounded-md hover:bg-[#1a2d5e] hover:text-white transition-colors">Admin</Link>
          ) : (
            <Link to="/admin/login" data-testid="nav-admin-login" className="px-4 py-2 text-sm font-semibold text-slate-500 hover:text-[#1a2d5e] transition-colors">Sign in</Link>
          )}
        </div>

        <button onClick={() => setOpen(!open)} className="lg:hidden p-2" data-testid="nav-mobile-toggle">
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden border-t border-slate-200 bg-white px-6 py-4 space-y-1" data-testid="nav-mobile-menu">
          {navItems.map((n) => (
            <NavLink key={n.to} to={n.to} onClick={() => setOpen(false)} className="block px-3 py-2 rounded-md text-slate-700 hover:bg-slate-100 font-medium">
              {n.label}
            </NavLink>
          ))}
          <div className="px-3 py-2"><CurrencySwitcher /></div>
          <Link to={user ? "/admin" : "/admin/login"} onClick={() => setOpen(false)} className="block px-3 py-2 rounded-md text-[#1a2d5e] font-semibold border border-slate-200 mt-2">
            {user ? "Admin Dashboard" : "Admin Sign in"}
          </Link>
        </div>
      )}
    </header>
  );
}

function Footer() {
  return (
    <footer className="bg-[#0b1838] text-slate-200 mt-24" data-testid="global-footer">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16 grid md:grid-cols-4 gap-10">
        <div>
          <div className="flex items-center gap-3 mb-4">
            <img src={LOGO_URL} alt="JAS" className="h-12 w-12 rounded-full" />
            <div>
              <div className="font-display font-black text-white text-lg">JAS</div>
              <div className="overline text-[10px] text-[#aedfff]">Unlimited Solutions</div>
            </div>
          </div>
          <p className="text-sm text-slate-400 leading-relaxed">Your trusted partner for phones, laptops, accessories, repairs and trade-in. Serving customers and shops since day one.</p>
        </div>

        <div>
          <div className="overline text-[#aedfff] mb-4">Shop</div>
          <ul className="space-y-2 text-sm">
            <li><Link to="/shop" className="hover:text-[#aedfff]">All Products</Link></li>
            <li><Link to="/shop?cat=phones" className="hover:text-[#aedfff]">Phones</Link></li>
            <li><Link to="/shop?cat=laptops" className="hover:text-[#aedfff]">Laptops</Link></li>
            <li><Link to="/shop?cat=accessories" className="hover:text-[#aedfff]">Accessories</Link></li>
          </ul>
        </div>

        <div>
          <div className="overline text-[#aedfff] mb-4">Services</div>
          <ul className="space-y-2 text-sm">
            <li><Link to="/repairs" className="hover:text-[#aedfff]">Phone &amp; Laptop Repairs</Link></li>
            <li><Link to="/trade-in" className="hover:text-[#aedfff]">Trade-in Program</Link></li>
            <li><Link to="/pos-system" className="hover:text-[#aedfff]">JAS POS for Shops</Link></li>
            <li><Link to="/app" className="hover:text-[#aedfff]">Mobile App</Link></li>
          </ul>
        </div>

        <div>
          <div className="overline text-[#aedfff] mb-4">Contact</div>
          <ul className="space-y-3 text-sm text-slate-300">
            <li className="flex items-center gap-2"><Phone size={14} /> Crieff: 01764 218483</li>
            <li className="flex items-center gap-2"><Phone size={14} /> Aberdeen: 07403 535385</li>
            <li className="flex items-center gap-2"><Mail size={14} /> info@jas-computers-mobiles.co.uk</li>
            <li className="flex items-center gap-2"><MapPin size={14} /> 60 High St, Crieff PH7 3BS</li>
            <li className="flex items-center gap-2"><MapPin size={14} /> Union Square, Aberdeen AB11 5RA</li>
            <li className="flex items-center gap-2 text-[#aedfff]"><ShieldCheck size={14} /> 12-Month Warranty</li>
          </ul>
        </div>
      </div>
      <div className="border-t border-white/10 py-5 text-center text-xs text-slate-500">
        © {new Date().getFullYear()} JAS Computers &amp; Mobiles — Unlimited Solutions. All rights reserved.
      </div>
    </footer>
  );
}

export default function Layout() {
  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <main>
        <Outlet />
      </main>
      <Footer />
      <WhatsAppFab />
    </div>
  );
}
